import React from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Chip,
} from '@mui/material'
import {
  ArrowLeft,
  Users,
  MessageCircle,
  BarChart3,
  Hash,
  TrendingUp,
} from 'lucide-react'
import { useGetAudienceInsightsQuery } from 'graphql/generated/graphql'

const platformEmoji: Record<string, string> = {
  Instagram: '📷',
  TikTok: '🎵',
  Youtube: '🎥',
  Reddit: '🔴',
  Facebook: '📘',
  Twitter: '🐦',
  Bluesky: '🦋',
}

interface AudienceInsightsProps {
  onBack: () => void
}

export default function AudienceInsights({ onBack }: AudienceInsightsProps): JSX.Element {
  const { data, loading } = useGetAudienceInsightsQuery()
  const insights = data?.getAudienceInsights

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#a855f7' }} />
      </Box>
    )
  }

  if (!insights || insights.totalCreators === 0) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <IconButton onClick={onBack} sx={{ color: 'rgba(255,255,255,0.7)' }}><ArrowLeft size={20} /></IconButton>
          <Typography sx={{ fontWeight: 700, color: 'white', fontSize: { xs: '18px', md: '24px' } }}>Audience Insights</Typography>
        </Box>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', py: 8 }}>
          No audience data yet. Engage with some content first to see insights.
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={onBack} sx={{ color: 'rgba(255,255,255,0.7)' }}><ArrowLeft size={20} /></IconButton>
        <Typography sx={{ fontWeight: 700, color: 'white', flex: 1, fontSize: { xs: '18px', md: '24px' } }}>
          Audience Insights
        </Typography>
      </Box>

      {/* Summary cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
        <Box sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Users size={16} color="#a855f7" />
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' }}>Creators Found</Typography>
          </Box>
          <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '28px' }}>{insights.totalCreators}</Typography>
        </Box>
        <Box sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <MessageCircle size={16} color="#a855f7" />
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' }}>Engagements</Typography>
          </Box>
          <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '28px' }}>{insights.totalEngagements}</Typography>
        </Box>
        <Box sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <BarChart3 size={16} color="#a855f7" />
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' }}>Platforms</Typography>
          </Box>
          <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '28px' }}>{insights.platformBreakdown.length}</Typography>
        </Box>
        <Box sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Hash size={16} color="#a855f7" />
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' }}>Topics</Typography>
          </Box>
          <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '28px' }}>{insights.topTopics.length}</Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
        {/* Platform Breakdown */}
        {insights.platformBreakdown.length > 0 && (
          <Box sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 2 }}>
              Where Your Audience Lives
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {insights.platformBreakdown.map((p) => (
                <Box key={p.platform} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography sx={{ fontSize: '16px', width: 24, textAlign: 'center' }}>{platformEmoji[p.platform] ?? '🌐'}</Typography>
                  <Typography sx={{ color: 'white', fontSize: '14px', fontWeight: 600, width: 80 }}>{p.platform}</Typography>
                  <Box sx={{ flex: 1, height: 8, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 4, overflow: 'hidden' }}>
                    <Box sx={{ width: `${String(p.percentage)}%`, height: '100%', background: 'linear-gradient(135deg, #a855f7, #6366f1)', borderRadius: 4 }} />
                  </Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', width: 40, textAlign: 'right' }}>
                    {String(p.percentage)}%
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Follower Tiers */}
        {insights.followerTiers.length > 0 && (
          <Box sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 2 }}>
              Creator Size Distribution
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {insights.followerTiers.map((tier) => (
                <Box key={tier.tier} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography sx={{ color: 'white', fontSize: '13px', fontWeight: 500, width: 120 }}>{tier.tier}</Typography>
                  <Box sx={{ flex: 1, height: 8, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 4, overflow: 'hidden' }}>
                    <Box sx={{ width: `${String(tier.percentage)}%`, height: '100%', background: 'linear-gradient(135deg, #22c55e, #16a34a)', borderRadius: 4 }} />
                  </Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', width: 50, textAlign: 'right' }}>
                    {String(tier.count)} ({String(tier.percentage)}%)
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Top Topics */}
      {insights.topTopics.length > 0 && (
        <Box sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px', mb: 3 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 2 }}>
            What Your Audience Talks About
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {insights.topTopics.map((topic, i) => (
              <Chip
                key={topic}
                label={topic}
                size="small"
                sx={{
                  backgroundColor: i < 3 ? 'rgba(168,85,247,0.15)' : 'rgba(255,255,255,0.05)',
                  border: i < 3 ? '1px solid rgba(168,85,247,0.3)' : '1px solid rgba(255,255,255,0.08)',
                  color: i < 3 ? '#a855f7' : 'rgba(255,255,255,0.6)',
                  fontSize: '12px',
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Engagement Trend */}
      {insights.engagementTrend.length > 0 && (
        <Box sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TrendingUp size={16} color="#a855f7" />
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Engagement Trend (14 days)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 80 }}>
            {insights.engagementTrend.map((day) => {
              const maxCount = Math.max(...insights.engagementTrend.map((d) => d.count), 1)
              const height = (day.count / maxCount) * 100
              return (
                <Box
                  key={day.date}
                  sx={{
                    flex: 1,
                    height: `${String(Math.max(height, 4))}%`,
                    background: day.count > 0 ? 'linear-gradient(180deg, #a855f7, #6366f1)' : 'rgba(255,255,255,0.05)',
                    borderRadius: '3px 3px 0 0',
                    minWidth: 8,
                  }}
                  title={`${day.date}: ${String(day.count)} engagements`}
                />
              )
            })}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '10px' }}>
              {insights.engagementTrend[0]?.date.slice(5) ?? ''}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '10px' }}>
              {insights.engagementTrend[insights.engagementTrend.length - 1]?.date.slice(5) ?? ''}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  )
}
